import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '@/components/UI/Button';
import { H2 } from '@/components/UI/Typography/Heading';
import { Modal } from '@mui/material';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import useResize from '@/hooks/useResize';
import { Flex } from '@/layout';
import PdfReader from '@/components/UI/PdfReader';
import { useGetPolicyTerms } from '@/hooks/useGetPolicyTerms';
import { STATIC_PICS_URL, STATIC_TERMS_URL } from '@/utils/policyDocuments';

interface openState {
  infoCollect: boolean;
  terms: boolean;
}

interface ModalProps {
  open: openState;
  handleClose: (key: string) => void;
  handleChange: (key: string, current: boolean) => void;
}

const StyledH2 = styled(H2)<{ isMobile: boolean }>`
  font-size: ${(p) => (p.isMobile ? p.theme.fontSizes.md : p.theme.fontSizes.lg)};
`;

export default function TermsModal({ open, handleClose, handleChange }: ModalProps) {
  // @ts-ignore
  const { t } = useTranslation();
  const { isMobile } = useResize();
  const { policyUrl, termsUrl } = useGetPolicyTerms();
  const [personalInfo, setPersonalInfo] = useState(true);
  const [termsInfo, setTermsInfo] = useState(true);
  const [PICPdfUrl, setPICPdfUrl] = useState(policyUrl);
  const [TermsPdfUrl, setTermsPdfUrl] = useState(termsUrl);
  const [isPersonalInfoLoading, setIsPersonalInfoLoading] = useState(true);
  const [isTermsLoading, setIsTermsLoading] = useState(true);
  const personalContent = useRef<HTMLDivElement>(null);
  const termsContent = useRef<HTMLDivElement>(null);
  const [personalReady, setPersonalReady] = useState(false);
  const [termsReady, setTermsReady] = useState(false);
  const handlePersonalReady = useCallback(() => setPersonalReady(true), []);
  const handleTermsReady = useCallback(() => setTermsReady(true), []);

  useEffect(() => { setPersonalReady(false); setPersonalInfo(true); }, [PICPdfUrl]);
  useEffect(() => { setTermsReady(false); setTermsInfo(true); }, [TermsPdfUrl]);
  // 最後一頁可能在捲動事件之後才畫完，完成時重新確認閱讀位置。
  useEffect(() => {
    const content = personalContent.current;
    if (personalReady && content && content.scrollHeight - content.scrollTop <= content.clientHeight + 2) setPersonalInfo(false);
  }, [personalReady]);
  useEffect(() => {
    const content = termsContent.current;
    if (termsReady && content && content.scrollHeight - content.scrollTop <= content.clientHeight + 2) setTermsInfo(false);
  }, [termsReady]);

  const personalHandler = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    if (isPersonalInfoLoading || !personalReady) return;
    const { currentTarget } = e;

    if (currentTarget.scrollHeight - currentTarget.scrollTop <= currentTarget.clientHeight + 2) {
      setPersonalInfo(false);
    }
  };

  const termHandler = (e: React.UIEvent<HTMLDivElement, UIEvent>) => {
    if (isTermsLoading || !termsReady) return;
    const { currentTarget } = e;

    if (currentTarget.scrollHeight - currentTarget.scrollTop <= currentTarget.clientHeight + 2) {
      setTermsInfo(false);
    }
  };

  const closeModal = (type: string, status: boolean) => {
    handleChange(type, status);
  };

  useEffect(() => {
    setPICPdfUrl(policyUrl);
  }, [policyUrl]);

  useEffect(() => {
    setTermsPdfUrl(termsUrl);
  }, [termsUrl]);

  const handlePicsError = useCallback(() => {
    setPersonalReady(false);
    setPersonalInfo(true);
    if (PICPdfUrl !== STATIC_PICS_URL) {
      setIsPersonalInfoLoading(true);
      setPICPdfUrl(STATIC_PICS_URL);
      return;
    }
    setIsPersonalInfoLoading(false);
  }, [PICPdfUrl]);

  const handleTermsError = useCallback(() => {
    setTermsReady(false);
    setTermsInfo(true);
    if (TermsPdfUrl !== STATIC_TERMS_URL) {
      setIsTermsLoading(true);
      setTermsPdfUrl(STATIC_TERMS_URL);
      return;
    }
    setIsTermsLoading(false);
  }, [TermsPdfUrl]);

  return (
    <>
      <Modal
        open={open.infoCollect}
        onClose={() => handleClose('infoCollect')}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        disableEnforceFocus
        sx={{ display: 'flex' }}
      >
        <div
          style={{
            margin: 'auto',
            width: '100%',
            maxWidth: '930px',
            backgroundColor: '#fff',
            padding: isMobile ? '15px 25px' : '30px 50px',
            borderRadius: '40px 40px 40px 0px',
          }}
        >
          <StyledH2 isMobile={isMobile}>{t('pages.auth.signUp.modal.terms.pic.title')}</StyledH2>
          <span style={{ font: 'Roboto 400 16px', lineHeight: '30px', color: 'red' }}>
            {t('pages.auth.signUp.modal.terms.pic.content')}
          </span>
          <div
            className="content"
            ref={personalContent}
            style={{
              marginTop: '25px',
              height: '40vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              fontFamily: 'Roboto',
              fontWeight: '400',
              fontSize: isMobile ? '16px' : '20px',
              color: '#616161',
              lineHeight: '30px',
            }}
            onScroll={personalHandler}
          >
            <PdfReader pdfUrl={PICPdfUrl} setIsLoading={setIsPersonalInfoLoading} onError={handlePicsError} onReady={handlePersonalReady} />
          </div>
          <Flex
            marginTop={'28px'}
            alignItems={'center'}
            justifyContent={'space-between'}
            flexDirection={isMobile ? 'column-reverse' : 'row'}
          >
            <Flex gridGap={isMobile ? 20 : 40} alignItems={'center'} flexDirection={isMobile ? 'column' : 'row'}>
              <Button
                width={isMobile ? '100%' : 'unset'}
                onClick={() => closeModal('infoCollect', personalInfo)}
                size={'lg'}
                variant={'primary'}
                rounded={false}
                disabled={personalInfo}
              >
                {t('buttons.agree')}
              </Button>
              <Button
                width={isMobile ? '100%' : 'unset'}
                color="primary"
                onClick={() => handleClose('infoCollect')}
                style={{ textDecoration: 'underline' }}
              >
                {t('buttons.back')}
              </Button>
            </Flex>
            <a target="_blank" rel="noreferrer" href={PICPdfUrl} style={{ alignSelf: isMobile ? 'flex-end' : 'unset' }}>
              <Button
                width={isMobile ? '100%' : 'unset'}
                color="primary"
                type="button"
                style={{ textDecoration: 'underline' }}
              >
                {t('buttons.download')}
              </Button>
            </a>
          </Flex>
        </div>
      </Modal>

      <Modal
        open={open.terms}
        onClose={() => handleClose('terms')}
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        disableEnforceFocus
        sx={{ display: 'flex' }}
      >
        <div
          style={{
            margin: 'auto',
            width: '100%',
            maxWidth: '930px',
            backgroundColor: '#fff',
            padding: isMobile ? '15px 25px' : '30px 50px',
            borderRadius: '40px 40px 40px 0px',
          }}
        >
          <StyledH2 isMobile={isMobile}>{t('pages.auth.signUp.modal.terms.t&c.title')}</StyledH2>
          <span style={{ font: 'Roboto 400 16px', lineHeight: '30px', color: 'red' }}>
            {t('pages.auth.signUp.modal.terms.t&c.title')}
          </span>
          <div
            className="content"
            ref={termsContent}
            style={{
              marginTop: '25px',
              height: '40vh',
              overflowY: 'auto',
              overflowX: 'hidden',
              fontFamily: 'Roboto',
              fontWeight: '400',
              fontSize: isMobile ? '16px' : '20px',
              color: '#616161',
              lineHeight: '30px',
            }}
            onScroll={termHandler}
          >
            <PdfReader pdfUrl={TermsPdfUrl} setIsLoading={setIsTermsLoading} onError={handleTermsError} onReady={handleTermsReady} />
          </div>
          <Flex
            marginTop={'28px'}
            alignItems={'center'}
            justifyContent={'space-between'}
            flexDirection={isMobile ? 'column-reverse' : 'row'}
          >
            <Flex gridGap={isMobile ? 20 : 40} alignItems={'center'} flexDirection={isMobile ? 'column' : 'row'}>
              <Button
                width={isMobile ? '100%' : 'unset'}
                onClick={() => closeModal('terms', termsInfo)}
                size={'lg'}
                variant={'primary'}
                rounded={false}
                disabled={termsInfo}
              >
                {t('buttons.agree')}
              </Button>
              <Button
                width={isMobile ? '100%' : 'unset'}
                color="primary"
                onClick={() => handleClose('terms')}
                style={{ textDecoration: 'underline' }}
              >
                {t('buttons.back')}
              </Button>
            </Flex>
            <a
              target="_blank"
              rel="noreferrer"
              href={TermsPdfUrl}
              style={{ alignSelf: isMobile ? 'flex-end' : 'unset' }}
            >
              <Button
                width={isMobile ? '100%' : 'unset'}
                color="primary"
                type="button"
                style={{ textDecoration: 'underline' }}
              >
                {t('buttons.download')}
              </Button>
            </a>
          </Flex>
        </div>
      </Modal>
    </>
  );
}
